/**
 * SessionMonitor — single handler for session expiry (401 after auth).
 * Mirrors ImpersonationExpiryMonitor: redirect once, show one notice on login.
 */

import SnackbarService from '@/services/SnackbarService.js';

const STORAGE_KEY = 'sessionMonitorState';
const QUIZ_RELOAD_NOTICE_KEY = 'sessionMonitorQuizReloadNotice';
const SESSION_EXPIRED_QUERY_PARAM = 'session_expired';
const SESSION_EXPIRED_MESSAGE =
    'Your session has expired. Please log in again to continue. Any unsaved work will be lost.';
// Mid-quiz the student's finalized answers are safe server-side, so the app-wide
// "unsaved work will be lost" copy is both alarming and inaccurate here (AC-7).
const SESSION_EXPIRED_QUIZ_MESSAGE = 'Your session has expired. Please log in again to continue.';
const PUBLIC_PATHS = ['/', '/login', '/password/email', '/password/reset'];
// The student quiz-taking route: /courses/:courseId?/activities/:activityId/quiz/:id[/:attemptId].
// Matches the attempt route only — NOT the quiz builder (/quizzes/...) or surveys.
const QUIZ_ATTEMPT_PATH_PATTERN = /\/activities\/[^/]+\/quiz\//;

const getStore = () => window.$nuxt?.$store || window.$store;

class SessionMonitor {
    constructor() {
        this.timeoutNotification = null;
        this._quizReloadPath = null;
        this._state = {
            sessionTimedOut: false,
            notificationShown: false,
            redirectInProgress: false,
            timeoutDetectedAt: null,
        };

        // A native reload reaches /login without the explicit expiry query flag.
        // Carry only this tab's quiz path across that load, never its auth state.
        try {
            const quizReloadPath = sessionStorage.getItem(QUIZ_RELOAD_NOTICE_KEY);
            sessionStorage.removeItem(QUIZ_RELOAD_NOTICE_KEY);
            this._quizReloadPath = quizReloadPath;
        } catch (_error) {
            // Storage can be unavailable; ordinary expiry recovery still works.
        }

        // Fresh page load must not inherit a prior tab's timed-out flags or
        // concurrent 401s would be suppressed with no redirect/notice.
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (_error) {
            // ignore
        }

        window.addEventListener('storage', event => this.handleStorageChange(event));

        if (!this.isPublicPath(window.location.pathname)) {
            setTimeout(() => this.setupStoreWatcher(), 0);
        }
    }

    setupStoreWatcher() {
        const store = getStore();
        if (!store) {
            return;
        }

        store.watch(
            state => state.Auth?.sessionTimedOut,
            newVal => {
                if (newVal === true) {
                    this.handleSessionTimeout();
                }
            }
        );
    }

    handleStorageChange(event) {
        if (event.key !== STORAGE_KEY || !event.newValue) {
            return;
        }

        try {
            const shared = JSON.parse(event.newValue);
            if (shared.sessionTimedOut && !this._state.sessionTimedOut) {
                this.handleSessionTimeout();
            }
        } catch (_error) {
            // ignore
        }
    }

    isPublicPath(path = window.location.pathname) {
        return PUBLIC_PATHS.some(p => path === p || (p !== '/' && path.startsWith(`${p}/`)));
    }

    /** Suppress duplicate unauthorized snackbars once expiry is being handled. */
    shouldSuppressError(error) {
        return error?.response?.status === 401 && (this._state.sessionTimedOut || this._state.redirectInProgress);
    }

    handleSessionTimeout() {
        if (this._state.redirectInProgress || this.isPublicPath()) {
            return;
        }

        if (this._state.sessionTimedOut) {
            // Already flagged on this page load. Mid-quiz, re-surface the recovery
            // snackbar (the student may have dismissed it or canceled the redirect);
            // everywhere else fall through to the app-wide redirect.
            if (this.isQuizAttemptContext()) {
                this.replaceTimeoutNotification();
            } else {
                this.redirectToLogin();
            }
            return;
        }

        this._state.sessionTimedOut = true;
        this._state.timeoutDetectedAt = new Date().toISOString();
        this.persistState();

        window.dispatchEvent(new CustomEvent('session-timeout'));

        // A hard redirect mid-quiz (especially lockdown) ejects the student, and if
        // they cancel the leave-site dialog it silently strands them ("works on a
        // different computer" incidents). Offer a soft-recovery snackbar and let them
        // choose when to leave (AC-7). Outside a quiz, keep the app-wide immediate
        // redirect master introduced.
        if (this.isQuizAttemptContext()) {
            try {
                sessionStorage.setItem(QUIZ_RELOAD_NOTICE_KEY, window.location.pathname);
            } catch (_error) {
                // The live recovery prompt does not depend on tab storage.
            }
            this.showTimeoutNotification();
        } else {
            this._state.notificationShown = true;
            this.persistState();
            this.redirectToLogin();
        }
    }

    /**
     * True while the student is on the quiz-taking route (a managed/lockdown quiz
     * attempt) — the surface AC-7's soft recovery is meant for.
     *
     * Deliberately route-based, NOT store-based: this app never exposes the Vuex
     * store on `window` (main.js does `app.use(store)` only), so `getStore()`
     * returns undefined and `Auth.user.quiz_active_id` can't be read here — and an
     * intervening background auth check (heartbeat 401) can reset the user to guest
     * before this runs anyway. The path is the dependable signal; it is still the
     * quiz-attempt route when the expiry is detected (the redirect hasn't happened
     * yet), which is why the pre-fix build hard-redirected instead of recovering.
     */
    isQuizAttemptContext(path = window.location.pathname) {
        return QUIZ_ATTEMPT_PATH_PATTERN.test(path);
    }

    /**
     * Soft-recovery prompt for the quiz-taking flow: one indefinite snackbar with a
     * "Log in now" action. The redirect only happens when the student chooses it, so
     * a mid-exam expiry never yanks them out from under an answer they're saving.
     */
    showTimeoutNotification() {
        if (this._state.notificationShown) {
            return;
        }

        this._state.notificationShown = true;
        this.persistState();

        try {
            this.timeoutNotification = SnackbarService.open({
                message: SESSION_EXPIRED_QUIZ_MESSAGE,
                type: 'is-warning',
                position: 'is-top',
                actionText: 'Log in now',
                indefinite: true,
                queue: false,
                onAction: () => this.redirectToLogin(),
            });
        } catch (_error) {
            // If the snackbar can't render, don't strand the student — redirect.
            setTimeout(() => this.redirectToLogin(), 1000);
        }
    }

    replaceTimeoutNotification() {
        // SnackbarService does not implement queue:false replacement. Remove only
        // our own prior notice before rearming; it may already have been dismissed.
        this.timeoutNotification?.close();
        this.timeoutNotification = null;
        this._state.notificationShown = false;
        this.persistState();
        this.showTimeoutNotification();
    }

    /** One lasting notice after landing on login (same pattern as ImpersonationExpiryMonitor). */
    maybeShowNotification(router) {
        const url = new URL(window.location.href);

        const explicitExpiry = url.searchParams.has(SESSION_EXPIRED_QUERY_PARAM);
        const redirect = url.searchParams.get('redirect');
        let redirectPath = null;
        if (redirect?.startsWith('/') && !redirect.startsWith('//')) {
            try {
                const target = new URL(redirect, url.origin);
                if (target.origin === url.origin) redirectPath = target.pathname;
            } catch (_error) {
                // A malformed return path cannot identify the expired quiz.
            }
        }
        // Manual login redirects retain the quiz path but drop its question query.
        const quizReload =
            url.pathname === '/login' &&
            this._quizReloadPath &&
            this.isQuizAttemptContext(this._quizReloadPath) &&
            redirectPath === this._quizReloadPath;
        this._quizReloadPath = null;

        if (!explicitExpiry && !quizReload) {
            return;
        }

        if (explicitExpiry) {
            url.searchParams.delete(SESSION_EXPIRED_QUERY_PARAM);

            // Keep Vue Router in sync while preserving the return path.
            if (router) {
                const query = Object.fromEntries(url.searchParams.entries());
                router.replace({ query }).catch(() => {});
            } else {
                window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            }
        }

        this.reset();

        SnackbarService.open({
            message: explicitExpiry ? SESSION_EXPIRED_MESSAGE : SESSION_EXPIRED_QUIZ_MESSAGE,
            type: 'is-warning',
            position: 'is-top',
            duration: 10000,
            queue: false,
        });
    }

    isNotificationShown() {
        return this._state.notificationShown;
    }

    isRedirectInProgress() {
        return this._state.redirectInProgress;
    }

    isSessionTimedOut() {
        return this._state.sessionTimedOut;
    }

    markRedirectInProgress() {
        this._state.redirectInProgress = true;
    }

    reset() {
        this._quizReloadPath = null;
        try {
            sessionStorage.removeItem(QUIZ_RELOAD_NOTICE_KEY);
        } catch (_error) {
            // ignore
        }
        this.timeoutNotification?.close();
        this.timeoutNotification = null;
        this._state = {
            sessionTimedOut: false,
            notificationShown: false,
            redirectInProgress: false,
            timeoutDetectedAt: null,
        };

        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (_error) {
            // ignore
        }
    }

    redirectToLogin() {
        if (this._state.redirectInProgress) {
            return;
        }

        this._state.redirectInProgress = true;

        const currentPath = window.location.pathname + window.location.search + window.location.hash;
        let loginUrl = `/login?${SESSION_EXPIRED_QUERY_PARAM}=1`;
        const pathOnly = window.location.pathname;

        if (pathOnly !== '/login' && pathOnly !== '/') {
            loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}&${SESSION_EXPIRED_QUERY_PARAM}=1`;

            try {
                getStore()?.dispatch('Auth/saveReturnPath', pathOnly);
            } catch (_error) {
                // ignore
            }
        }

        try {
            window.location.replace(loginUrl);
        } catch (_error) {
            window.location.href = loginUrl;
        }

        // If the browser navigation was canceled (the quiz lockdown page installs
        // a beforeunload leave-site dialog students instinctively cancel mid-exam),
        // execution continues here with redirectInProgress still set — every later
        // recovery attempt on this page load would otherwise be silently blocked.
        // Never leave the suppression flag wedged.
        setTimeout(() => {
            if (!this._state.redirectInProgress) {
                return;
            }

            this._state.redirectInProgress = false;

            // Mid-quiz, re-surface the recovery snackbar rather than forcing another
            // redirect (which would just re-trigger the leave-site dialog): the
            // student keeps working until they choose to log in (AC-7). Outside a
            // quiz, retry the redirect as before.
            if (this.isQuizAttemptContext()) {
                this.replaceTimeoutNotification();
            } else {
                this.redirectToLogin();
            }
        }, 4000);
    }

    persistState() {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    sessionTimedOut: this._state.sessionTimedOut,
                    notificationShown: this._state.notificationShown,
                    timeoutDetectedAt: this._state.timeoutDetectedAt,
                })
            );
        } catch (_error) {
            // ignore
        }
    }
}

const sessionMonitor = new SessionMonitor();
window.SessionMonitor = sessionMonitor;

export default sessionMonitor;
export { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_QUERY_PARAM, SESSION_EXPIRED_QUIZ_MESSAGE };
